import { useMemo, useState, type KeyboardEvent } from 'react';
import type { TagEntity } from '../../../entities/task/model/task.types';
import type { TaskListItem } from '../model/task.types';
import { PRIORITY_BAR_CLASS } from '../../../shared/lib/priority';

function tagChipStyle(color: string): { backgroundColor: string; borderColor: string; color: string } {
  return {
    backgroundColor: `${color}22`,
    borderColor: `${color}70`,
    color,
  };
}

export function TagChip({ tag }: { tag: TagEntity }) {
  const style = tagChipStyle(tag.color);
  return (
    <span
      className="inline-flex max-w-24 items-center truncate rounded-full border px-1.5 py-px text-[10px] leading-4"
      style={style}
      title={tag.name}
    >
      {tag.name}
    </span>
  );
}

/** Чипы тегов строки: на узком экране 1–2 + «+N». */
export function TaskTagChips({ tags, maxVisible = 2 }: { tags: TagEntity[]; maxVisible?: number }) {
  const visible = tags.slice(0, maxVisible);
  const rest = tags.length - visible.length;
  if (tags.length === 0) {
    return null;
  }
  return (
    <div className="flex shrink-0 items-center gap-1">
      {visible.map((tag) => (
        <TagChip key={tag.id} tag={tag} />
      ))}
      {rest > 0 ? (
        <span className="text-[10px] text-tg-text-muted" title={tags.slice(maxVisible).map((t) => t.name).join(', ')}>
          +{rest}
        </span>
      ) : null}
    </div>
  );
}

/** Прогресс чек-листа вида 2/5. */
export function ChecklistProgress({
  done,
  total,
}: {
  done: number;
  total: number;
}) {
  if (total <= 0) {
    return null;
  }
  return (
    <span
      className={`inline-flex items-center rounded px-1 text-[10px] leading-4 ${
        done === total ? 'text-tg-success' : 'text-tg-text-secondary'
      }`}
      title={`Чек-лист: ${done} из ${total}`}
    >
      ✅ {done}/{total}
    </span>
  );
}

export interface TaskRowProps {
  item: TaskListItem;
  isDesktop: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onOpen: () => void;
  onToggleComplete: () => void;
  onArchive?: () => void;
  /** Восстановить из архива (только экран архива). */
  onRestore?: () => void;
  /** Безвозвратное удаление (только экран архива), с подтверждением у родителя. */
  onDeleteForever?: () => void;
  onSelectToggle?: () => void;
}

/**
 * Компактная строка задачи (~36px desktop / ≤44px mobile).
 * Приоритет — полоской слева. Чекбокс — отдельная зона нажатия.
 * urgent: пульсирующая полоска + мигающий флажок (prefers-reduced-motion — без анимации).
 */
export function TaskRow({
  item,
  isDesktop,
  selectionMode = false,
  selected = false,
  onOpen,
  onToggleComplete,
  onArchive,
  onRestore,
  onDeleteForever,
  onSelectToggle,
}: TaskRowProps) {
  const priority = item.task.priority;
  const completed = item.isCompleted;
  const isUrgent = priority === 'urgent' && !completed;

  const bar = PRIORITY_BAR_CLASS[priority];

  const bg = isUrgent
    ? 'tg-urgent-row hover:bg-tg-danger-soft/70'
    : priority === 'high' && !completed
      ? 'bg-tg-danger-soft/40 hover:bg-tg-danger-soft/70'
      : completed
        ? 'opacity-70 hover:bg-tg-surface-hover'
        : 'hover:bg-tg-surface-hover';

  const titleClass = [
    'min-w-0 flex-1 truncate text-sm',
    isUrgent ? 'font-bold text-tg-text' : '',
    priority === 'high' && !completed ? 'font-semibold text-tg-text' : '',
    priority === 'low' && !completed ? 'text-tg-text-secondary' : '',
    completed ? 'line-through text-tg-text-muted' : 'text-tg-text',
  ]
    .filter(Boolean)
    .join(' ');

  function handleClick(): void {
    if (selectionMode) {
      onSelectToggle?.();
      return;
    }
    if (!isDesktop) {
      onOpen();
    }
  }

  function handleDoubleClick(): void {
    if (selectionMode) {
      return;
    }
    onOpen();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    if (selectionMode) {
      onSelectToggle?.();
      return;
    }
    onOpen();
  }

  return (
    <div
      role={selectionMode ? 'option' : 'button'}
      aria-selected={selectionMode ? selected : undefined}
      tabIndex={0}
      className={`group flex min-h-11 w-full items-center gap-2 border-l-0 border-tg-border-soft/60 px-2 py-1.5 text-left transition-colors md:min-h-9 ${bar} ${bg} ${
        selectionMode && selected ? 'ring-1 ring-tg-accent/50' : ''
      }`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      title={item.task.title}
    >
      {selectionMode ? (
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-tg-accent"
          checked={selected}
          aria-label={`Выбрать задачу ${item.task.title}`}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={() => {
            onSelectToggle?.();
          }}
        />
      ) : (
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          aria-label={completed ? 'Снять отметку выполнения' : 'Отметить выполненной'}
          aria-pressed={completed}
          onClick={(event) => {
            event.stopPropagation();
            onToggleComplete();
          }}
        >
          <span
            className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 ${
              completed
                ? 'border-tg-accent bg-tg-accent text-tg-bg-deep'
                : priority === 'urgent' || priority === 'high'
                  ? 'border-tg-priority-high/80 text-transparent'
                  : 'border-tg-text-muted text-transparent'
            }`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      )}

      <span className={titleClass}>{item.task.title}</span>

      <TaskTagChips tags={item.tags} maxVisible={2} />

      <div className="flex shrink-0 items-center gap-1 text-[11px] text-tg-text-muted">
        {item.isRecurring ? (
          <span title="Повторяющаяся задача" aria-label="Повторяющаяся">
            🔁
          </span>
        ) : null}
        {item.hasDescription ? (
          <span title="Есть описание" aria-label="Есть описание">
            📄
          </span>
        ) : null}
        {item.hasChecklist ? (
          <ChecklistProgress done={item.checklistDone} total={item.checklistTotal} />
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {priority === 'urgent' && !completed ? (
          <span
            className={`tg-urgent-flag text-xs font-bold text-tg-priority-urgent`}
            title="Очень срочно!"
            aria-label="Очень срочно!"
          >
            ⚑
          </span>
        ) : null}
        {priority === 'high' && !completed ? (
          <span className="text-xs text-tg-priority-high" title="Срочно" aria-label="Срочно">
            ⚑
          </span>
        ) : null}
        {item.hasTime && item.task.due_time !== null ? (
          <span
            className={`text-xs tabular-nums ${
              completed ? 'text-tg-text-muted' : 'text-tg-text-secondary'
            }`}
          >
            {item.task.due_time}
          </span>
        ) : null}
        {!selectionMode && onArchive !== undefined ? (
          <button
            type="button"
            className="rounded-md p-1 text-tg-text-muted opacity-0 transition-opacity hover:text-tg-text focus-visible:opacity-100 group-hover:opacity-100 md:opacity-60"
            aria-label={`Отправить в архив: ${item.task.title}`}
            title="В архив"
            onClick={(event) => {
              event.stopPropagation();
              onArchive();
            }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="4" y="6" width="16" height="4" rx="1" />
              <path d="M6 10v8a2 2 0 002 2h8a2 2 0 002-2v-8" strokeLinecap="round" />
              <path d="M10 14h4" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
        {!selectionMode && onRestore !== undefined ? (
          <button
            type="button"
            className="rounded-md p-1 text-tg-text-muted opacity-0 transition-opacity hover:text-tg-accent focus-visible:opacity-100 group-hover:opacity-100 md:opacity-60"
            aria-label={`Восстановить: ${item.task.title}`}
            title="Восстановить"
            onClick={(event) => {
              event.stopPropagation();
              onRestore();
            }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M4 12a8 8 0 1 0 2.4-5.7" strokeLinecap="round" />
              <path d="M4 4v5h5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
        {!selectionMode && onDeleteForever !== undefined ? (
          <button
            type="button"
            className="ml-2 rounded-md p-1 text-tg-text-muted opacity-0 transition-opacity hover:text-tg-danger focus-visible:opacity-100 group-hover:opacity-100 md:opacity-60"
            aria-label={`Удалить навсегда: ${item.task.title}`}
            title="Удалить навсегда"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteForever();
            }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M4 7h16" strokeLinecap="round" />
              <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" />
              <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" strokeLinecap="round" />
              <path d="M10 11v6M14 11v6" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Хук для мемоизации не нужен — строки пересобираются селекторами данных. */
export function useTagIdSet(tags: TagEntity[]): Set<string> {
  return useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);
}

export function useSelectionSet(): {
  ids: Set<string>;
  toggle: (id: string) => void;
  clear: () => void;
  setMany: (ids: string[]) => void;
} {
  const [ids, setIds] = useState<Set<string>>(() => new Set());
  return {
    ids,
    toggle: (id: string) => {
      setIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    clear: () => {
      setIds(new Set());
    },
    setMany: (list: string[]) => {
      setIds(new Set(list));
    },
  };
}
